import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { loadBasket } from "./loadBasket";

/**
 * Arguments for addVoucherCode thunk.
 */
interface AddVoucherCodeArgs {
    code: string;
}

/**
 * Error response shape from voucher API.
 */
interface VoucherErrorResponse {
    non_field_errors?: string[];
    vouchercode?: string[];
    reason?: string;
}

/**
 * Add a voucher code to the basket.
 *
 * This thunk applies a promotional voucher code to the user's basket
 * and reloads the basket to reflect any discounts or offers. Used in
 * checkout flows and cart views for applying promo codes.
 *
 * @param {string} args.code - The voucher code to apply
 * @returns {Promise<void>} Promise resolving when voucher is applied and basket reloaded
 *
 * @example
 * // Apply a promo code
 * dispatch(addVoucherCode({ code: 'SUMMER2024' }));
 *
 * @throws {VoucherErrorResponse} If voucher code is invalid or expired (from API)
 *
 * @sideEffects
 * - Makes API call to add voucher via basketAPI.addVoucherCode
 * - Dispatches loadBasket thunk to reload basket data with applied discount
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer and pricing recalculations on the backend
 */
export const addVoucherCode = createAsyncThunk<
    void,
    AddVoucherCodeArgs,
    { rejectValue: VoucherErrorResponse }
>("checkout/addVoucherCode", async (args, { dispatch, rejectWithValue }) => {
    try {
        await basketAPI.addVoucherCode(args.code);
        await dispatch(loadBasket()).unwrap();
    } catch (err: unknown) {
        // Extract the error response body for the component to display
        const error = err as { response?: { body?: VoucherErrorResponse } };
        if (error.response?.body) {
            return rejectWithValue(error.response.body);
        }
        throw err;
    }
});
